import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";

/**
 * Pre-first-upload media consent.
 *   - Clear language about storage and AI analysis
 *   - Confirms user controls (delete videos, delete account, export)
 *   - Records consent server-side (version + timestamp)
 */
export default function MediaConsentModal({ sessionId, onAccept, onCancel }) {
  const [busy, setBusy] = useState(false);

  async function accept() {
    setBusy(true);
    try {
      await api.post("/web/consent", { session_id: sessionId, media_consent: true });
      onAccept?.();
    } catch {
      // Allow the user to proceed even if logging consent fails — but warn.
      onAccept?.();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/75 flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="media-consent-modal"
    >
      <div
        className="w-full sm:max-w-md bg-[#111] border border-neutral-800 rounded-t-3xl sm:rounded-3xl p-6"
        style={{ paddingBottom: "calc(1.5rem + var(--gully-bottom-nav-h, env(safe-area-inset-bottom, 0px)))" }}
      >
        <div className="text-base font-medium mb-3">Before you upload your first video</div>
        <div className="text-sm text-neutral-300 leading-relaxed space-y-3 mb-5">
          <p>
            You're uploading a cricket video to Gully for AI analysis.
          </p>
          <p>
            Your video may be stored so Gully can review it, track your progress, and compare future
            clips. Videos are private — never shared with other users.
          </p>
          <p className="text-neutral-400 text-xs">
            You can delete your videos or your account at any time from{" "}
            <Link to="/me" className="text-emerald-400 hover:underline">account settings</Link>.
            See <Link to="/trust" className="text-emerald-400 hover:underline">Trust & Privacy</Link>.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="flex-1 py-3 rounded-xl border border-neutral-800 text-neutral-300 hover:bg-neutral-900"
            data-testid="media-consent-cancel"
          >
            Not yet
          </button>
          <button
            onClick={accept}
            disabled={busy}
            className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 font-medium"
            data-testid="media-consent-accept"
          >
            {busy ? "Saving…" : "I understand"}
          </button>
        </div>
      </div>
    </div>
  );
}
